import { captureException } from '@sentry/react';
import { useCallback, useEffect, useRef } from 'react';

const dialogPolyfill = typeof HTMLDialogElement === 'function' ? null : import('dialog-polyfill').then((mod) => mod.default);
export const useDialog = () => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const closeOnClickOutside = useCallback((e: MouseEvent) => {
    if (e.target instanceof HTMLDialogElement) {
      const { x, y, width, height } = e.target.getBoundingClientRect();
      // click outside dialog horisontal
      const outsideHorisontal = e.clientX < x || e.clientX > x + width;
      const outsideVertical = e.clientY < y || e.clientY > y + height;
      if (outsideHorisontal || outsideVertical) {
        e.target.close();
      }
    }
  }, []);
  // register dialog polyfills, and wire up events
  useEffect(() => {
    const element = ref.current;
    if (dialogPolyfill && element) {
      import('dialog-polyfill/dialog-polyfill.css').catch(() => captureException('Failed to lazy-load dialog-polyfill CSS'));
      dialogPolyfill
        .then((polyfill) => {
          polyfill.registerDialog(element);
        })
        .catch(() => captureException('Failed to lazy-load dialog-polyfill'));
    }
    // Handle click on outside to close
    if (element) {
      element.addEventListener('click', closeOnClickOutside);
      return () => {
        element.removeEventListener('click', closeOnClickOutside);
      };
    }
  }, [closeOnClickOutside]);

  const closeDialog = useCallback(() => ref.current?.close(), []);
  const openDialog = useCallback(() => ref.current?.show(), []);
  const openModalDialog = useCallback(() => ref.current?.showModal(), []);

  return { ref, closeDialog, openDialog, openModalDialog };
};
